import PaymentMethod from '@alltrails/analytics/enums/PaymentMethod';
import WebProPurchaseLocation from '@alltrails/analytics/enums/WebProPurchaseLocation';
import proApi from 'api/Pro';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import logError from 'utils/logError';
import { handlePaymentFormSuccess } from 'utils/Payment';
import { execute } from 'utils/recaptcha_helpers';
import Typography from 'components/Typography';
import ProResponse from 'types/ProResponse';
import { getPriceTotal } from 'utils/PlanUtils';
import payPalLogoSrc from './paypal-logo.svg';
import * as styles from './PayPalButton.module.scss';

type Props = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  plan: Plan;
  recurlyPublicKey: string;
  setErrors: (errors: ProResponse['errors']) => void;
  userId: number;
};

// Desktop: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c1bcf26ba802db57c180b
// Mobile: https://app.zeplin.io/project/627c18d6c6189e2a52ce6566/screen/627c241f824169281f996dc1
export default function ApplePayButton({ coupon, currencyCode, plan, recurlyPublicKey, userId, setErrors }: Props) {
  const languageRegionCode = useLanguageRegionCode();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    recurly.configure(recurlyPublicKey);
  }, []);

  function handleClick() {
    const payPal = recurly.PayPal({
      display: {
        displayName: 'AllTrails Pro',
        // @ts-ignore - this is valid https://github.com/recurly/recurly-js/blob/4d702a0e6b514441cb3a3b41d528e214ab39df29/lib/recurly/paypal/strategy/direct.js#L16
        amount: String(getPriceTotal(plan, coupon))
      }
    });

    payPal.on('error', error => {
      logError(error);
    });

    payPal.on('token', async token => {
      try {
        setSubmitting(true);

        const recaptcha = await execute('userPaymentAction');

        const body = {
          recurly_plan_code: plan.recurlyPlanCode,
          paypal_token: token.id,
          recaptcha,
          uid: userId,
          user: {
            user_entered_coupon_code: coupon ? coupon.code : null
          }
        };

        const response = await proApi(body);

        if (response?.errors) {
          setErrors(response.errors);
        } else {
          handlePaymentFormSuccess(
            userId,
            currencyCode,
            coupon,
            plan,
            languageRegionCode,
            coupon?.code,
            WebProPurchaseLocation.ProPage,
            PaymentMethod.Paypal
          );
        }
      } catch (e) {
        setErrors(e);
      } finally {
        setSubmitting(false);
      }
    });

    payPal.start();
  }

  return (
    <button className={styles.button} disabled={submitting} onClick={handleClick} type="button">
      <Typography variant="headline2">
        <FormattedMessage defaultMessage="Continue with" />
      </Typography>
      &nbsp;
      <img alt="" className={styles.logo} src={payPalLogoSrc} />
    </button>
  );
}
